import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../header/Header";
import Footer from "../footer/Footer";
import { BUSINESS, LEGAL_ROUTES } from "../../config/business";
import "./policy.css";

/**
 * Shared renderer for every legal / policy page.
 *
 * props:
 *  - title:    page heading
 *  - intro:    short paragraph under the heading
 *  - sections: [{ heading, body: [ string | {list: [..]} | {table: {head, rows}} ] }]
 */
export default function PolicyPage({ title, intro, sections = [], currentPath }) {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${title} | ${BUSINESS.brand}`;
  }, [title]);

  return (
    <div className="policy-shell">
      <Header />

      <main className="policy-main">
        <div className="policy-hero">
          <div className="policy-hero-inner">
            <p className="policy-eyebrow">Legal</p>
            <h1 className="policy-title">{title}</h1>
            {intro && <p className="policy-intro">{intro}</p>}
            <p className="policy-updated">
              Last updated: {BUSINESS.policyLastUpdated} &nbsp;·&nbsp; All prices
              and payments on this website are in {BUSINESS.currencyLabel}.
            </p>
          </div>
        </div>

        <div className="policy-body">
          <aside className="policy-nav" aria-label="Policy navigation">
            <p className="policy-nav-title">Policies</p>
            <ul>
              {LEGAL_ROUTES.map((r) => (
                <li key={r.path}>
                  <Link
                    to={r.path}
                    className={r.path === currentPath ? "is-active" : ""}
                  >
                    {r.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="policy-nav-card">
              <p className="policy-nav-title">Need help?</p>
              <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>
              <a href={`tel:+${BUSINESS.phoneRaw}`}>{BUSINESS.phone}</a>
              <span className="policy-nav-hours">{BUSINESS.supportHours}</span>
            </div>
          </aside>

          <article className="policy-content">
            {sections.map((section, i) => (
              <section key={i} className="policy-section">
                <h2>
                  <span className="policy-section-num">{i + 1}.</span>{" "}
                  {section.heading}
                </h2>
                {section.body.map((block, j) => {
                  if (typeof block === "string") {
                    return <p key={j}>{block}</p>;
                  }
                  if (block.list) {
                    return (
                      <ul key={j} className="policy-list">
                        {block.list.map((item, k) => (
                          <li key={k}>{item}</li>
                        ))}
                      </ul>
                    );
                  }
                  if (block.table) {
                    return (
                      <div key={j} className="policy-table-wrap">
                        <table className="policy-table">
                          <thead>
                            <tr>
                              {block.table.head.map((h, k) => (
                                <th key={k}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {block.table.rows.map((row, k) => (
                              <tr key={k}>
                                {row.map((cell, l) => (
                                  <td key={l}>{cell}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  }
                  if (block.note) {
                    return (
                      <div key={j} className="policy-note">
                        {block.note}
                      </div>
                    );
                  }
                  return null;
                })}
              </section>
            ))}

            <section className="policy-section policy-contact-block">
              <h2>Contact &amp; Grievance Redressal</h2>
              <p>
                For any question, complaint or dispute relating to this policy,
                an order, a payment or your personal data, contact us first — we
                aim to acknowledge every complaint within 48 working hours and
                resolve it within 15 working days.
              </p>
              <dl className="policy-dl">
                <div>
                  <dt>Legal entity</dt>
                  <dd>
                    {BUSINESS.legalName} ({BUSINESS.entityType})
                  </dd>
                </div>
                {BUSINESS.gstin && (
                  <div>
                    <dt>GSTIN</dt>
                    <dd>{BUSINESS.gstin}</dd>
                  </div>
                )}
                {BUSINESS.cin && (
                  <div>
                    <dt>CIN</dt>
                    <dd>{BUSINESS.cin}</dd>
                  </div>
                )}
                <div>
                  <dt>Registered address</dt>
                  <dd>{BUSINESS.registeredAddress}</dd>
                </div>
                <div>
                  <dt>Showroom / operating address</dt>
                  <dd>{BUSINESS.operatingAddress}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>
                    <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>
                  </dd>
                </div>
                <div>
                  <dt>Phone / WhatsApp</dt>
                  <dd>
                    <a href={`tel:+${BUSINESS.phoneRaw}`}>{BUSINESS.phone}</a>
                  </dd>
                </div>
                <div>
                  <dt>Support hours</dt>
                  <dd>{BUSINESS.supportHours}</dd>
                </div>
              </dl>
            </section>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}
